import {
  Blueprint,
  BlueprintExpense,
  BlueprintGovernance,
  BlueprintProduct,
  BlueprintResource,
} from "../../../pages/Projects/ServicesV2/types";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import DataTable from "@components/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import "./style.css";
import { useV2GetRateTableQuery, useV2GetPaymentTermQuery } from "@generated";

interface BlueprintItem {
  id: string;
  name?: string;
  quantity?: string;
  resource?: { id: string; name: string };
}

interface GeneralRow {
  id: string;
  name: string;
  value: string;
}

interface BlueprintPreviewProps {
  blueprint: Blueprint;
  slideoutOpen: boolean;
  setSlideoutOpen: (bool) => void;
  title?: string;
}

const BlueprintPreview = ({
  blueprint,
  slideoutOpen,
  setSlideoutOpen,
  title = "View Services In Blueprint",
}: BlueprintPreviewProps) => {
  const allServices = blueprint?.services || [];
  const professionalServices = allServices.filter(
    (s) => s.serviceType === "professional_services"
  );
  const managedServices = allServices.filter(
    (s) => s.serviceType !== "professional_services"
  );

  const general = blueprint?.general;
  const rateTableId = general?.rateTableId;
  const paymentTermId = general?.paymentTermId;

  const { data: rateTableData } = useV2GetRateTableQuery(
    { id: rateTableId ?? "" },
    { skip: !rateTableId }
  );
  const { data: paymentTermData } = useV2GetPaymentTermQuery(
    { id: paymentTermId ?? "" },
    { skip: !paymentTermId }
  );

  const rateTableName = rateTableData?.data?.attributes?.name;
  const paymentTermName = paymentTermData?.data?.attributes?.name;

  const generalRows: GeneralRow[] = [];
  if (general?.executiveSummary)
    generalRows.push({
      id: "executive-summary",
      name: "Executive Summary",
      value: general.executiveSummary,
    });
  if (general?.solutionSummary)
    generalRows.push({
      id: "solution-summary",
      name: "Solution Summary",
      value: general.solutionSummary,
    });
  if (rateTableName)
    generalRows.push({
      id: "rate-table",
      name: "Rate Table",
      value: rateTableName,
    });
  if (paymentTermName)
    generalRows.push({
      id: "payment-term",
      name: "Payment Term",
      value: paymentTermName,
    });

  const columns: ColumnDef<BlueprintItem>[] = [
    {
      header: "Service Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Resource",
      cell(props) {
        return <div>{props.row.original?.resource?.name || ""}</div>;
      },
    },
  ];

  const generalColumns: ColumnDef<GeneralRow>[] = [
    {
      header: "Item Name",
      cell(props) {
        return props.row.original.name;
      },
    },
    {
      header: "Value",
      cell(props) {
        return <div className="generalValue">{props.row.original.value}</div>;
      },
    },
  ];

  const governanceColumns: ColumnDef<BlueprintGovernance>[] = [
    {
      header: "Description",
      cell(props) {
        return props.row.original.description || "";
      },
    },
    {
      header: () => <div className="alignRight">Hours</div>,
      id: "hours",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.hours || ""}</div>
        );
      },
    },
  ];

  const expenseColumns: ColumnDef<BlueprintExpense>[] = [
    {
      header: "Description",
      cell(props) {
        return props.row.original.description || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Rate",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.rate || ""}</div>
        );
      },
    },
  ];

  const resourceColumns: ColumnDef<BlueprintResource>[] = [
    {
      header: "Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: () => <div className="alignRight">Hours</div>,
      id: "hours",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.hours || ""}</div>
        );
      },
    },
    {
      header: () => <div className="alignRight">Rate</div>,
      id: "rate",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.rate || ""}</div>
        );
      },
    },
  ];

  const productColumns: ColumnDef<BlueprintProduct>[] = [
    {
      header: "Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Unit Price",
      cell(props) {
        return (
          <div className="alignCenter">
            {props.row.original.unitPrice || ""}
          </div>
        );
      },
    },
  ];

  const governances = blueprint?.governances || [];
  const expenses = blueprint?.expenses || [];
  const resources = blueprint?.resources || [];
  const products = blueprint?.products || [];

  const hasAnyContent =
    generalRows.length > 0 ||
    allServices.length > 0 ||
    governances.length > 0 ||
    expenses.length > 0 ||
    resources.length > 0 ||
    products.length > 0;

  return (
    <SlideOutMenuV2
      title={title}
      isOpen={slideoutOpen}
      onClose={() => setSlideoutOpen(false)}
    >
      <div className="blueprintPreviewSlideout">
        {generalRows.length > 0 && (
          <div>
            <div className="previewSubheader">General</div>
            <DataTable
              data={generalRows}
              columns={generalColumns}
              bordered
              striped
            />
          </div>
        )}

        {professionalServices.length > 0 && (
          <div>
            <div className="previewSubheader">Professional Services</div>
            <DataTable
              data={professionalServices}
              columns={columns}
              bordered
              striped
            />
          </div>
        )}

        {managedServices.length > 0 && (
          <div>
            <div className="previewSubheader">Managed Services</div>
            <DataTable
              data={managedServices}
              columns={columns}
              bordered
              striped
            />
          </div>
        )}

        {governances.length > 0 && (
          <div>
            <div className="previewSubheader">Governance</div>
            <DataTable
              data={governances}
              columns={governanceColumns}
              bordered
              striped
            />
          </div>
        )}

        {expenses.length > 0 && (
          <div>
            <div className="previewSubheader">Expenses</div>
            <DataTable
              data={expenses}
              columns={expenseColumns}
              bordered
              striped
            />
          </div>
        )}

        {resources.length > 0 && (
          <div>
            <div className="previewSubheader">Resources</div>
            <DataTable
              data={resources}
              columns={resourceColumns}
              bordered
              striped
            />
          </div>
        )}

        {products.length > 0 && (
          <div>
            <div className="previewSubheader">Products</div>
            <DataTable
              data={products}
              columns={productColumns}
              bordered
              striped
            />
          </div>
        )}

        {!hasAnyContent && "No services found"}
      </div>
    </SlideOutMenuV2>
  );
};

export default BlueprintPreview;
